import React, { useState } from "react";
import * as XLSX from "xlsx";
import { parseScheduleShifts } from "./TechnicianScheduleUtils";

const TechnicianSchedule = ({
  year, month, technicians, schedule, onTechClick,
  photosByTech = {}, onUploadPhoto, onViewPhoto,
  destinations = {}, caseCounts = {}, destinationStatus = "loading",
}) => {
  const today = new Date();
  const days = new Date(year, month + 1, 0).getDate();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;
  const weeks = [];
  for (let day = 1; day <= days; day++) {
    if (day === 1 || new Date(year, month, day).getDay() === 1) weeks.push([]);
    weeks[weeks.length - 1].push(day);
  }
  const initialDay = isCurrentMonth ? today.getDate() : 1;
  const [selectedDay, setSelectedDay] = useState(initialDay);
  const [selectedTech, setSelectedTech] = useState("");
  const [view, setView] = useState("month");
  const [week, setWeek] = useState(weeks.findIndex((dates) => dates.includes(initialDay)));
  const [search, setSearch] = useState("");
  const [team, setTeam] = useState("");
  const [shiftFilter, setShiftFilter] = useState("");
  const firstDay = view === "month" ? 1 : weeks[week][0];
  const lastDay = view === "month" ? days : weeks[week][weeks[week].length - 1];
  const visibleDays = Array.from({ length: lastDay - firstDay + 1 }, (_, index) => firstDay + index);
  const availableTechnicians = technicians.filter((tech) => tech.status === "Available");
  const teams = Array.from(new Set(availableTechnicians.map((tech) => tech.team).filter(Boolean)));
  const matchesShift = (tech, day) => {
    if (!shiftFilter) return true;
    const shifts = parseScheduleShifts(schedule[`${tech.name}-${day}`]);
    return shiftFilter === "DN"
      ? shifts.includes("D") && shifts.includes("N")
      : shifts.includes(shiftFilter);
  };
  const visibleTechnicians = availableTechnicians.filter((tech) =>
    (!team || tech.team === team) &&
    `${tech.nickname} ${tech.name}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()) &&
    (!shiftFilter || visibleDays.some((day) => matchesShift(tech, day)))
  );
  const formatDate = (day) => new Date(year, month, day).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  const missingDestination = destinationStatus === "loading" ? "Loading sites…" :
    destinationStatus === "error" ? "Sites unavailable" : "No booking shown";

  const selectDay = (day, techName = "") => {
    setSelectedDay(day);
    setSelectedTech(techName);
  };
  const getDayAssignment = (tech, day) => {
    const key = `${tech.name}-${day}`;
    const shifts = parseScheduleShifts(schedule[key]);
    const sites = destinationStatus === "ready" ? destinations[key] || [] : [];
    const caseCount = destinationStatus === "ready" ? caseCounts[key] || 0 : null;
    return { shifts, sites, caseCount, mismatch: sites.length > 0 && (!shifts.length || shifts.includes("OFF")) };
  };
  const dayAssignments = visibleTechnicians.filter((tech) => matchesShift(tech, selectedDay))
    .map((tech) => ({ tech, ...getDayAssignment(tech, selectedDay) }));
  const selectedAssignment = dayAssignments.find(({ tech }) => tech.name === selectedTech);
  const detailAssignments = selectedAssignment ? [selectedAssignment] : dayAssignments;
  const daySummary = {
    cases: dayAssignments.reduce((sum, { caseCount }) => sum + (caseCount || 0), 0),
    night: dayAssignments.filter(({ shifts }) => shifts.includes("N")).length,
    booked: dayAssignments.filter(({ sites }) => sites.length > 0).length,
    unassigned: dayAssignments.filter(({ shifts, sites }) => shifts.includes("N") && !sites.length).length,
    mismatch: dayAssignments.filter(({ mismatch }) => mismatch).length,
  };
  const renderShiftDetails = (tech, day, expanded = false) => {
    const { shifts, sites, caseCount, mismatch } = getDayAssignment(tech, day);
    return <React.Fragment>
      <div className="tech-shift-badges">
        {shifts.length ? shifts.map((shift) => <span key={shift} className={`tech-shift-${shift.toLowerCase()}`} title={shift === "N" ? "Night shift" : shift === "D" ? "Day shift" : "Off duty"}><b>{shift}</b>{expanded && shift !== "OFF" && <span className="tech-shift-description">{shift === "N" ? "Night" : "Day"}</span>}</span>) : <span className="tech-empty-shift">No shift</span>}
      </div>
      {caseCount !== null && <div className="tech-case-count">{caseCount} {caseCount === 1 ? "case" : "cases"}</div>}
      {sites.map((site) => <div key={site} className="tech-destination">{site}</div>)}
      {shifts.includes("N") && !sites.length && <div className="tech-destination-empty">{missingDestination}</div>}
      {mismatch && <div className="tech-schedule-mismatch">Check shift{expanded && <small>Booking exists; shift is OFF or blank.</small>}</div>}
    </React.Fragment>;
  };

  const handleExportExcel = () => {
    const headers = ["Team", "Name", ...Array.from({ length: days }, (_, i) => i + 1)];
    const rows = visibleTechnicians.map((tech) => [tech.team, tech.nickname,
      ...Array.from({ length: days }, (_, i) => {
        const key = `${tech.name}-${i + 1}`;
        const shifts = parseScheduleShifts(schedule[key]);
        const sites = destinationStatus === "ready" ? destinations[key] || [] : [];
        return [shifts.join(" "), destinationStatus === "ready" ? `Cases: ${caseCounts[key] || 0}` : "Cases unavailable", ...sites,
          shifts.includes("N") && !sites.length ? missingDestination : ""].filter(Boolean).join("\n");
      }),
    ]);
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Schedule");
    XLSX.writeFile(workbook, `Schedule-${year}-${String(month + 1).padStart(2, "0")}.xlsx`);
  };

  return (
    <div className="tech-schedule-container">
      <div className="tech-schedule-toolbar">
        <div>
          <h3 className="mb-1">Technician Schedule</h3>
          <span className="tech-schedule-subtitle">Select a date for locations. Select a shift to focus on one technician.</span>
        </div>
        <button className="btn btn-outline-primary btn-sm" onClick={handleExportExcel}>Export to Excel</button>
      </div>
      <div className="tech-schedule-toolbar tech-filter-bar">
        <div className="tech-schedule-filters">
          <input type="search" className="form-control" aria-label="Search technicians" placeholder="Search technicians" value={search} onChange={(event) => setSearch(event.target.value)} />
          <select className="form-control" aria-label="Team" value={team} onChange={(event) => setTeam(event.target.value)}>
            <option value="">All teams</option>
            {teams.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
          <select className="form-control" aria-label="Shift" value={shiftFilter} onChange={(event) => setShiftFilter(event.target.value)}>
            <option value="">All shifts</option>
            <option value="D">D · Day (includes DN)</option>
            <option value="N">N · Night (includes DN)</option>
            <option value="DN">DN · Day + Night</option>
          </select>
          <button className="btn btn-link btn-sm" onClick={() => { setSearch(""); setTeam(""); setShiftFilter(""); }}>Reset filters</button>
        </div>
        <div className="btn-group" aria-label="Schedule view">
          {["month", "week"].map((mode) => <button key={mode} className={`btn btn-sm btn-${view === mode ? "primary" : "outline-primary"}`} aria-pressed={view === mode} onClick={() => { if (mode === "week") setWeek(weeks.findIndex((dates) => dates.includes(selectedDay))); setView(mode); }}>{mode === "week" ? "Week" : "Month"}</button>)}
        </div>
      </div>
      {shiftFilter && <p className="tech-schedule-subtitle">Showing technicians with {shiftFilter === "DN" ? "D + N on the same day" : `${shiftFilter} (including DN)`} on at least one day in this {view}. Daily assignments match the selected date.</p>}
      <div className="tech-schedule-toolbar">
        <div className="tech-schedule-range">
          {view === "week" && <button className="btn btn-outline-secondary btn-sm" aria-label="Previous week" disabled={week === 0} onClick={() => setWeek(week - 1)}>‹</button>}
          <strong>{formatDate(firstDay)} – {formatDate(lastDay)} {year}</strong>
          {view === "week" && <button className="btn btn-outline-secondary btn-sm" aria-label="Next week" disabled={lastDay === days} onClick={() => setWeek(week + 1)}>›</button>}
        </div>
        <div className="tech-schedule-legend"><span><b className="tech-legend-day">D</b> Day shift</span><span><b className="tech-legend-night">N</b> Night shift</span><span><b className="tech-legend-off">OFF</b> Off duty</span></div>
      </div>
      {view === "month" && <div className="tech-month-legend">
        <span><b className="tech-indicator-booked">2</b> Number of cases</span>
        <span><b className="tech-indicator-empty">0</b> No cases shown</span>
        <span><b className="tech-indicator-warning">2</b> Cases · Check shift</span>
        <span className="tech-month-legend-hint">Click a week heading for details.</span>
      </div>}
      {destinationStatus === "error" && <div className="alert alert-warning py-2" role="alert">Booking destinations could not be loaded. Please reload to try again.</div>}
      <div className="tech-schedule-scroll tech-schedule-scroll--technician" tabIndex={0} role="region" aria-label="Technician shifts and destinations">
        <table className={`tech-schedule-table tech-schedule-table--${view}`}>
          <thead>
            {view === "month" && <tr className="tech-week-groups">
              <th className="tech-name-heading" rowSpan={2} scope="col">Technician / Team</th>
              {weeks.map((dates, index) => <th key={index} colSpan={dates.length} scope="colgroup">
                <button className="tech-week-link" title={`Open week ${index + 1}: ${formatDate(dates[0])} – ${formatDate(dates[dates.length - 1])}`} onClick={() => { setWeek(index); setView("week"); }}>W{index + 1}</button>
              </th>)}
              <th rowSpan={2} scope="col" className="tech-photo-heading">Photo</th>
            </tr>}
            <tr>
              {view === "week" && <th scope="col" className="tech-name-heading">Technician / Team</th>}
              {visibleDays.map((day) => {
                const date = new Date(year, month, day);
                const isToday = isCurrentMonth && day === today.getDate();
                return <th scope="col" key={day} className={`${[0, 6].includes(date.getDay()) ? "tech-weekend" : ""} ${date.getDay() === 1 ? "tech-week-start" : ""} ${isToday ? "tech-today" : ""} ${view === "month" && selectedDay === day ? "tech-selected-date" : ""}`}>
                  {view === "month" ? <button className="tech-date-button" aria-label={`Show ${formatDate(day)} locations`} aria-pressed={selectedDay === day} aria-controls="tech-day-details" title={date.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })} onClick={() => selectDay(day)}>
                    <span className="tech-date-number">{day}</span>
                    <span>{date.toLocaleDateString("en-GB", { weekday: "short" }).slice(0, 2)}</span>
                  </button> : <React.Fragment>
                    <span className="tech-date-number">{day}</span>
                    <span>{date.toLocaleDateString("en-GB", { weekday: "short" })}</span>
                    {isToday && <small className="d-block">Today</small>}
                  </React.Fragment>}
                </th>;
              })}
              {view === "week" && <th scope="col" className="tech-photo-heading">Photo</th>}
            </tr>
          </thead>
          <tbody>
            {visibleTechnicians.map((tech, index) => {
              const isLastOfCenter = index === visibleTechnicians.length - 1 || tech.team !== visibleTechnicians[index + 1].team;
              const photoUrl = (photosByTech[tech.name] || {}).url || "";
              return (
                <tr key={tech.name} className={`${isLastOfCenter ? "last-center-row" : ""} ${selectedTech === tech.name ? "tech-selected-row" : ""}`}>
                  <th scope="row" className="tech-name-cell">
                    <button className="tech-name-button" onClick={() => onTechClick(tech.name)} title={`Edit schedule: ${tech.name}`}>{tech.nickname || tech.name}</button>
                    <span className="tech-team-label">{tech.team || "No team"}</span>
                  </th>
                  {visibleDays.map((day) => {
                    const { shifts, sites, caseCount, mismatch } = getDayAssignment(tech, day);
                    const hasNight = shifts.includes("N");
                    const detail = `${tech.nickname || tech.name}, ${formatDate(day)}: ${shifts.join(" / ") || "No shift"}; ${caseCount === null ? "Case count unavailable" : `${caseCount} ${caseCount === 1 ? "case" : "cases"}`}${sites.length ? `; ${sites.join("; ")}` : hasNight ? `; ${missingDestination}` : ""}${mismatch ? "; Check shift" : ""}`;
                    return <td key={day} className={`${hasNight ? "tech-night-cell" : ""} ${mismatch ? "tech-conflict-cell" : ""} ${hasNight && !sites.length && destinationStatus === "ready" ? "tech-unassigned-cell" : ""} ${new Date(year, month, day).getDay() === 1 ? "tech-week-start" : ""} ${isCurrentMonth && day === today.getDate() ? "tech-today" : ""} ${view === "month" && selectedDay === day ? "tech-selected-date" : ""}`}>
                      {view === "month" ? <button className="tech-month-cell" title={detail} aria-label={detail} aria-controls="tech-day-details" aria-pressed={selectedDay === day && selectedTech === tech.name} onClick={() => selectDay(day, tech.name)}>
                        <span className="tech-shift-badges">
                          {shifts.length ? shifts.map((shift) => <span key={shift} className={`tech-shift-${shift.toLowerCase()}`}>{shift}</span>) : <span className="tech-empty-shift">—</span>}
                        </span>
                        <span aria-hidden="true" className={`tech-month-indicator ${mismatch ? "tech-indicator-warning" : caseCount > 0 ? "tech-indicator-booked" : hasNight ? "tech-indicator-empty" : "tech-indicator-zero"}`}>{caseCount === null ? "?" : caseCount}</span>
                      </button> : renderShiftDetails(tech, day)}
                    </td>;
                  })}
                  <td className="tech-photo-cell">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 6,
                        minWidth: 30,
                      }}
                    >
                      {/* Hidden file input */}
                      <input
                        id={"file-" + tech.name}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={function (e) {
                          var f = e.target.files && e.target.files[0];
                          if (f && onUploadPhoto) onUploadPhoto(tech.name, f);
                          e.target.value = ""; // allow re-selecting the same file
                        }}
                      />

                      {/* Upload icon (click to open file chooser) */}
                      <label
                        htmlFor={"file-" + tech.name}
                        title={`Upload schedule image for ${tech.nickname || tech.name}`}
                        aria-label={`Upload schedule image for ${tech.nickname || tech.name}`}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            document.getElementById("file-" + tech.name).click();
                          }
                        }}
                        style={{
                          width: 25,
                          height: 25,
                          borderRadius: 6,
                          border: "1px solid #ced4da",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          background: "#fff",
                        }}
                      >
                        <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>+</span>
                      </label>

                      {/* Thumbnail (click to view in modal) */}
                      {photoUrl ? (
                        <img
                          src={photoUrl}
                          alt={tech.nickname + " photo"}
                          onClick={function () {
                            if (onViewPhoto) onViewPhoto(tech.name);
                          }}
                          style={{
                            width: 25,
                            height: 25,
                            objectFit: "cover",
                            borderRadius: 6,
                            border: "1px solid #dee2e6",
                            cursor: "pointer",
                          }}
                        />
                      ) : (
                        <div
                          title="No image yet"
                          style={{
                            width: 25,
                            height: 25,
                            borderRadius: 6,
                            border: "1px dashed #ced4da",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 10,
                            color: "#999",
                            background: "#fafafa",
                          }}
                        >
                          No image
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!visibleTechnicians.length && <tr><td colSpan={visibleDays.length + 2} className="p-4">No technicians match the current filters.</td></tr>}
          </tbody>
        </table>
      </div>
      {view === "month" && <React.Fragment>
        <section id="tech-day-details" className="tech-day-details" aria-label="Selected date locations">
          <div className="tech-schedule-toolbar tech-details-heading">
            <div>
              <span className="tech-section-label">Daily assignments</span>
              <h4 className="mb-0">{new Date(year, month, selectedDay).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</h4>
            </div>
            <div className="tech-detail-navigation">
              <button className="btn btn-outline-secondary btn-sm" aria-label="Previous day" disabled={selectedDay === 1} onClick={() => selectDay(selectedDay - 1)}>‹</button>
              <button className="btn btn-outline-secondary btn-sm" aria-label="Next day" disabled={selectedDay === days} onClick={() => selectDay(selectedDay + 1)}>›</button>
              <button className="btn btn-outline-primary btn-sm" onClick={() => { setWeek(weeks.findIndex((dates) => dates.includes(selectedDay))); setView("week"); }}>Open this week</button>
            </div>
          </div>
          <div className="tech-day-summary" aria-label="Selected day summary">
            <span><strong>{daySummary.night}</strong> Night shifts</span>
            {destinationStatus === "ready" ? <React.Fragment>
              <span className="tech-summary-booked"><strong>{daySummary.cases}</strong> Cases</span>
              <span className="tech-summary-booked"><strong>{daySummary.booked}</strong> With bookings</span>
              <span className="tech-summary-unassigned"><strong>{daySummary.unassigned}</strong> N · No booking shown</span>
              <span className="tech-summary-warning"><strong>{daySummary.mismatch}</strong> Check shift</span>
            </React.Fragment> : <span>{destinationStatus === "loading" ? "Loading booking summary…" : "Booking summary unavailable"}</span>}
          </div>
          {selectedAssignment && <div className="tech-detail-focus">
            <span>Showing <strong>{selectedAssignment.tech.nickname || selectedAssignment.tech.name}</strong></span>
            <button className="btn btn-link btn-sm" onClick={() => setSelectedTech("")}>Show all technicians</button>
          </div>}
          <div className="tech-day-list">
            {detailAssignments.map(({ tech }) => <div key={tech.name} className={`tech-day-assignment ${selectedTech === tech.name ? "tech-day-assignment--selected" : ""}`}>
              <div className="tech-assignment-person"><strong>{tech.nickname || tech.name}</strong><span className="tech-team-label">{tech.team || "No team"}</span></div>
              <div className="tech-assignment-info">{renderShiftDetails(tech, selectedDay, true)}</div>
              <button className="btn btn-outline-secondary btn-sm tech-edit-shifts" aria-label={`Edit ${tech.nickname || tech.name} shifts`} onClick={() => onTechClick(tech.name)}>Edit shifts</button>
            </div>)}
            {!detailAssignments.length && <p className="tech-details-empty">No technicians match the current filters on this date.</p>}
          </div>
          <p className="tech-details-scope">Locations use Latest Sleep Study Date and Recording Tech, regardless of the current booking status or event date.</p>
        </section>
      </React.Fragment>}
      <div className="tech-schedule-footer">{visibleTechnicians.length} technicians · Click a technician’s name to edit shifts. Export includes the full month for the filtered technicians.</div>
    </div>
  );
};

export default TechnicianSchedule;
